import { useSearchParams, useNavigate } from 'react-router';
import { resultsSearchParamsSchema, type ResultsSearchParams } from '../domain/schemas';
import { useQuery } from '@tanstack/react-query';
import resultsService from '../domain/resultsService';
import routes from '../../routing/routes';

export function useResultsViewModel() {
  const [searchParams] = useSearchParams();
  const parsedSearchParams = parseSearchParams(searchParams);

  const resultsQuery = useQuery({
    queryKey: ['results', parsedSearchParams],
    queryFn: async () => {
      if (!parsedSearchParams) {
        return [];
      }
      return resultsService.getResults(parsedSearchParams.category, parsedSearchParams.flavours, parsedSearchParams.tastes, parsedSearchParams.mouthfeels);
    },
  });

  const navigate = useNavigate();
  const resetWizard = () => {
    return navigate(routes.createWizardCategoryUrl());
  };

  const currentStep = parseSearchParams(searchParams);
  const goToPreviousStep = () => {
    if(currentStep) {
      return navigate(routes.createWizardMouthfeelUrl(currentStep.category, currentStep.flavours, currentStep.tastes, currentStep.mouthfeels));
    }
  };
  const goToFirstStep = () => {
    if(currentStep) {
      return navigate(routes.createWizardFlavourUrl(currentStep.category, []));
    }
  };

  const canSearch = () => {
    let hasFilterData = false;

    if (parsedSearchParams) {
      hasFilterData =
        parsedSearchParams.flavours.length > 0 ||
        parsedSearchParams.tastes.length > 0 ||
        parsedSearchParams.mouthfeels.length > 0;
    }

    return hasFilterData;
  };

  return {
    resetWizard,
    goToPreviousStep,
    goToFirstStep,
    canSearch: canSearch(),
    results: resultsQuery.data ?? [],
    isLoading: resultsQuery.isLoading,
    error: resultsQuery.error || parsedSearchParams === null,
  };
}

const parseSearchParams = (searchParams: URLSearchParams): ResultsSearchParams | null => {
  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const parsed = resultsSearchParamsSchema.safeParse(searchParamsObject);

  if (!parsed.success) {
    console.warn('Invalid search params', parsed.error);
    return null;
  }

  return parsed.data;
};
